import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {IconArchive, IconArchiveOff, IconEdit, IconPlus} from '@tabler/icons-react'
import {useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {LocationEditModal} from 'src/jobs/location-edit-modal'
import type {JobLocation} from 'src/jobs/types'

import type {CreateEquipmentYardInput, EquipmentYard} from './schemas'
import {
    useAllEquipmentYards,
    useCreateEquipmentYard,
    useUpdateEquipmentYard,
} from './use-equipment-yards'

type ModalMode = 'closed' | 'create-new' | 'edit'

function yardToJobLocation(yard: EquipmentYard): JobLocation {
    return {
        name: yard.name,
        notes: '',
        positionMode: yard.fullAddress ? 'address' : 'pin',
        latitude: yard.latitude,
        longitude: yard.longitude,
        addressText: yard.fullAddress,
        fromSavedLocationId: null,
        rememberAsSavedLocation: false,
        savedLocationMode: 'create-new',
    }
}

function jobLocationToYardInput(location: JobLocation): CreateEquipmentYardInput {
    return {
        name: location.name,
        latitude: location.latitude ?? 0,
        longitude: location.longitude ?? 0,
        fullAddress:
            location.positionMode === 'address' && location.addressText?.trim()
                ? location.addressText.trim()
                : null,
    }
}

export function EquipmentYardsSection() {
    const user = useCurrentUser()
    const admin = isAdmin(user)
    const {
        data: yards = [],
        isLoading,
        isError,
        error,
        refetch,
    } = useAllEquipmentYards({enabled: admin})
    const createYard = useCreateEquipmentYard()
    const updateYard = useUpdateEquipmentYard()
    const [modalMode, setModalMode] = useState<ModalMode>('closed')
    const [editingYard, setEditingYard] = useState<EquipmentYard | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    if (!admin) {
        return null
    }

    function startAddingYard() {
        setModalMode('create-new')
        setEditingYard(null)
        setErrorMessage(null)
    }

    function startEditing(yard: EquipmentYard) {
        setModalMode('edit')
        setEditingYard(yard)
        setErrorMessage(null)
    }

    function closeModal() {
        setModalMode('closed')
        setEditingYard(null)
        setErrorMessage(null)
    }

    async function save(location: JobLocation) {
        const input = jobLocationToYardInput(location)
        if (modalMode === 'edit' && editingYard) {
            await updateYard.mutateAsync({id: editingYard.id, input})
        } else {
            await createYard.mutateAsync(input)
        }
    }

    async function toggleArchived(yard: EquipmentYard) {
        setErrorMessage(null)
        try {
            await updateYard.mutateAsync({id: yard.id, input: {active: !yard.active}})
        } catch (toggleError) {
            setErrorMessage(
                toggleError instanceof Error ? toggleError.message : 'Unable to update yard',
            )
        }
    }

    if (isLoading) {
        return <Loader size="sm" />
    }

    if (isError) {
        return (
            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Title order={3}>Yards</Title>
                    <Alert color="red" title="Could not load equipment yards">
                        {error instanceof Error ? error.message : 'Something went wrong.'}
                    </Alert>
                    <Button onClick={() => void refetch()} variant="light">
                        Retry
                    </Button>
                </Stack>
            </Paper>
        )
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Yards</Title>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        variant="light"
                        size="compact-sm"
                        onClick={startAddingYard}
                    >
                        Add yard
                    </Button>
                </Group>

                {errorMessage && modalMode === 'closed' ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                {yards.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No yards configured yet.
                    </Text>
                ) : (
                    <Stack gap="sm">
                        {yards.map((yard) => (
                            <Group
                                key={yard.id}
                                justify="space-between"
                                align="flex-start"
                                wrap="nowrap"
                            >
                                <Stack gap={0} style={{minWidth: 0}}>
                                    <Group gap="xs">
                                        <Text fw={500}>{yard.name}</Text>
                                        <Badge color={yard.active ? 'green' : 'gray'} size="sm">
                                            {yard.active ? 'Active' : 'Archived'}
                                        </Badge>
                                    </Group>
                                    {yard.fullAddress ? (
                                        <Text size="sm" c="dimmed">
                                            {yard.fullAddress}
                                        </Text>
                                    ) : null}
                                    <Text size="xs" c="dimmed">
                                        {yard.latitude.toFixed(6)}, {yard.longitude.toFixed(6)}
                                    </Text>
                                </Stack>
                                <Group gap="xs">
                                    <ActionIcon
                                        size="sm"
                                        variant="default"
                                        aria-label={`Edit ${yard.name}`}
                                        onClick={() => startEditing(yard)}
                                        disabled={updateYard.isPending}
                                    >
                                        <IconEdit size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                        size="sm"
                                        variant="default"
                                        aria-label={`${yard.active ? 'Archive' : 'Reactivate'} ${yard.name}`}
                                        onClick={() => void toggleArchived(yard)}
                                        disabled={updateYard.isPending}
                                    >
                                        {yard.active ? (
                                            <IconArchive size={16} />
                                        ) : (
                                            <IconArchiveOff size={16} />
                                        )}
                                    </ActionIcon>
                                </Group>
                            </Group>
                        ))}
                    </Stack>
                )}

                <LocationEditModal
                    isOpen={modalMode !== 'closed'}
                    onClose={closeModal}
                    onSave={save}
                    mode={modalMode === 'closed' ? 'create-new' : modalMode}
                    source="equipment_yard_editor"
                    initialLocation={
                        modalMode === 'edit' && editingYard
                            ? yardToJobLocation(editingYard)
                            : undefined
                    }
                    customerId={null}
                    title={modalMode === 'edit' ? 'Edit yard' : 'Add yard'}
                    nameLabel="Yard name"
                    showNotes={false}
                    showSaveCheckbox={false}
                />
            </Stack>
        </Paper>
    )
}
